import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { formatRp } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { HandCoins, Plus, Save, Trash2, Users } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const emptyRecipient = () => ({ id: `${Date.now()}-${Math.random()}`, name: '', percentage: '' });

export default function ProfitSharingSection({ pid, nilaiRab, nilaiKeuntungan }) {
  const { can } = useAuth();
  const canWrite = can('business.write');
  const [source, setSource] = useState('rab');
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const baseValue = source === 'rab' ? Number(nilaiRab || 0) : Number(nilaiKeuntungan || 0);
  const sourceLabel = source === 'rab' ? 'Nilai RAB' : 'Nilai Keuntungan';
  const totalPercentage = useMemo(
    () => recipients.reduce((sum, item) => sum + (Number(item.percentage) || 0), 0),
    [recipients],
  );
  const isValid = recipients.length > 0 && Math.abs(totalPercentage - 100) < 0.01 && recipients.every((item) => item.name.trim() && Number(item.percentage) > 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${pid}/profit-sharing/${source}`);
      setRecipients((data.recipients || []).map((item) => ({ ...item, percentage: String(item.percentage ?? '') })));
    } catch {
      toast.error('Gagal memuat pengaturan profit sharing');
      setRecipients([]);
    } finally {
      setLoading(false);
    }
  }, [pid, source]);

  useEffect(() => { load(); }, [load]);

  const updateRecipient = (id, field, value) => {
    setRecipients((current) => current.map((item) => item.id === id ? { ...item, [field]: value } : item));
  };

  const save = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${pid}/profit-sharing/${source}`, {
        recipients: recipients.map(({ name, percentage }) => ({ name: name.trim(), percentage: Number(percentage) })),
      });
      setRecipients(data.recipients.map((item) => ({ ...item, percentage: String(item.percentage) })));
      toast.success(`Pengaturan dari ${sourceLabel} berhasil disimpan`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal menyimpan pengaturan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div role="tabpanel" className="space-y-5 animate-in-up">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold"><HandCoins className="h-4 w-4 text-[var(--fpccs-primary)]" /> Profit Sharing</h2>
          <p className="mt-1 text-xs text-subtle-fpccs">Atur penerima dan persentase pembagian untuk project ini.</p>
        </div>
        <div className="rounded-xl border border-[var(--fpccs-border)] bg-[var(--fpccs-surface-2)] p-3 text-right">
          <div className="text-[11px] text-subtle-fpccs">Dasar pembagian: {sourceLabel}</div>
          <div className="mt-1 font-mono text-lg font-semibold">{formatRp(baseValue)}</div>
        </div>
      </div>

      <div className="flex w-full gap-2 rounded-xl bg-[var(--fpccs-surface-2)] p-1 sm:w-fit">
        {[['rab', 'Dari Nilai RAB'], ['profit', 'Dari Nilai Keuntungan']].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setSource(key)} className={`rounded-lg px-3 py-2 text-xs font-medium transition-colors sm:px-4 ${source === key ? 'bg-[var(--fpccs-primary)] text-white shadow-sm' : 'text-muted-fpccs hover:text-[var(--fpccs-text)]'}`}>{label}</button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-[var(--fpccs-border)]" aria-label="Tabel pembagian profit, geser ke samping untuk melihat semua kolom">
        <div className="min-w-[560px]">
          <div className="grid grid-cols-[minmax(180px,1fr)_120px_minmax(155px,0.8fr)_44px] gap-3 bg-[var(--fpccs-surface-2)] px-4 py-3 text-[11px] font-medium uppercase tracking-wide text-subtle-fpccs">
            <span>Nama Penerima</span><span>Persentase</span><span className="text-right">Nilai Bagian</span><span />
          </div>
          {loading ? <div className="px-4 py-10 text-center text-xs text-subtle-fpccs">Memuat pengaturan...</div> : recipients.length === 0 ? <div className="px-4 py-10 text-center text-xs text-subtle-fpccs"><Users className="mx-auto mb-2 h-5 w-5" />Belum ada penerima. Tambahkan penerima pertama.</div> : (
            <div className="divide-y divide-[var(--fpccs-border)]">
              {recipients.map((item) => {
                const part = baseValue * ((Number(item.percentage) || 0) / 100);
                return <div key={item.id} className="grid grid-cols-[minmax(180px,1fr)_120px_minmax(155px,0.8fr)_44px] items-center gap-3 px-4 py-3">
                  <Input disabled={!canWrite} value={item.name} onChange={(e) => updateRecipient(item.id, 'name', e.target.value)} placeholder="Contoh: Agi" className="h-9" />
                  <div className="relative"><Input disabled={!canWrite} type="number" min="0" max="100" step="0.01" value={item.percentage} onChange={(e) => updateRecipient(item.id, 'percentage', e.target.value)} placeholder="0" className="h-9 pr-7" /><span className="absolute right-2.5 top-2 text-xs text-subtle-fpccs">%</span></div>
                  <div className="text-right font-mono text-sm font-medium whitespace-nowrap">{formatRp(part)}</div>
                  {canWrite && <button type="button" onClick={() => setRecipients((current) => current.filter((recipient) => recipient.id !== item.id))} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--fpccs-danger)] hover:bg-red-50" aria-label={`Hapus ${item.name || 'penerima'}`}><Trash2 className="h-4 w-4" /></button>}
                </div>;
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {canWrite && <Button type="button" variant="outline" onClick={() => setRecipients((current) => [...current, emptyRecipient()])} className="gap-2 sm:w-auto"><Plus className="h-4 w-4" /> Tambah Penerima</Button>}
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <div className={`rounded-lg px-3 py-2 text-xs ${Math.abs(totalPercentage - 100) < 0.01 ? 'bg-emerald-50 text-[var(--fpccs-success)]' : 'bg-red-50 text-[var(--fpccs-danger)]'}`}>
            Total persentase: <strong>{totalPercentage.toFixed(2)}%</strong>{Math.abs(totalPercentage - 100) >= 0.01 && ` • Sisa ${(100 - totalPercentage).toFixed(2)}%`}
          </div>
          {canWrite && <Button type="button" disabled={!isValid || saving} onClick={save} className="gap-2"><Save className="h-4 w-4" /> {saving ? 'Menyimpan...' : 'Simpan Pengaturan Profit Sharing'}</Button>}
        </div>
      </div>
      {!isValid && !loading && <p className="text-xs text-subtle-fpccs">Isi semua nama penerima dan pastikan total persentase tepat 100% untuk menyimpan.</p>}
    </div>
  );
}
